<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance, toRaw, watch, reactive, onMounted } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Divider from 'primevue/divider';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { useToast } from 'primevue/usetoast';
import FloatLabel from 'primevue/floatlabel';
import InputNumber from 'primevue/inputnumber';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import ContentModal from '@/components/ContentModal.vue';
import PublicationSection from '@/components/admin/orders/PublicationSection.vue';
import ContentReviewSection from '@/components/admin/orders/ContentReviewSection.vue';
import ArticleApprovalSection from '@/components/admin/orders/ArticleApprovalSection.vue';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import { useLoading } from '@/composables/useLoading';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useOrderItemActions } from '@/composables/useOrderItemActions';
import OrderItemActionsDialogs from '@/components/admin/orders/OrderItemActionsDialogs.vue';
import { useOrderStatus } from '@/composables/useOrderStatus';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const selectedItems = ref([]);
const siteFeatures = ref(null);
const selectedOrderItem = ref(null);
const submittingContent = ref(false);
const currentItemIndex = ref(null);
const itemContents = reactive({});
const expandedContentItems = ref({});
const showContentModal = ref(false);
const openLinkDialog = ref(false);
const openEditDialog = ref(false);
const editingCurrency = ref('USD');
const linkValue = ref('');
const editingColumn = ref('');
const editingRow = ref('');
const editingSite = ref(null);

defineOptions({
    layout: AdminMainLayout,
});

const { formatWithSymbol, formatWithSymbolRaw, formatConvertedPrice, getCurrentCurrency, onCurrencyChange, exchangeRates, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const formattedLanguages = computed(() => {
    if (!languages) return [];

    return languages
        .map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const { order, coins, statuses, clients, sites, types, payment_settings, languages } = defineProps({
    order: Object,
    coins: Object,
    statuses: Object,
    clients: Array,
    sites: Object,
    types: Array,
    payment_settings: Object,
    languages: Array,
});

// --- Proper cancel/add item actions (operate immediately on saved items) ---
// These act on the persisted order (props.order.order_items) via the shared
// composable, independent of the form's pending edits. The form's own raw
// add/remove mechanics are left untouched.
const { canEditItems, canCancelItem } = useOrderStatus();
const itemActions = useOrderItemActions({
    getOrderId: () => order.id,
    onSuccess: () => router.reload({ preserveScroll: true }),
});

const savedOrderItems = () => order.orderItems || order.order_items || [];
const activeItemCount = computed(() => savedOrderItems().filter((i) => !i.canceled_at).length);

// A canceled order is fully read-only on this screen.
const isCanceled = computed(() => order.status === 'CANCELED');

// Map a form.sites row back to its persisted order item. In this form each row's
// `id` is the SITE id while `pivot_id` is the ORDER ITEM id, so saved rows are
// matched by pivot_id. Staged (unsaved) rows have no matching persisted item and
// return null, so the immediate cancel action only appears on already-saved items.
const savedItemFor = (row) => {
    if (!row || !row.pivot_id) return null;
    const found = savedOrderItems().find((i) => i.id === row.pivot_id);
    if (!found) return null;
    return { ...found, site_url: found.site?.url || row.url };
};

const rowClass = (row) => {
    const saved = savedItemFor(row);
    return saved && saved.canceled_at ? 'opacity-60 line-through' : '';
};

// Unified per-row remove affordance. Saved items are soft-canceled through the
// service (marks canceled_at, emails the client, writes a change-log); staged
// (unsaved) rows have no server-side counterpart yet, so they are just removed
// from the local form. Callers must guard on canEditItems(order) for the lock.
const removeItem = (row, index) => {
    const saved = savedItemFor(row);
    if (saved) {
        itemActions.openCancelItem(saved);
    } else {
        remove(index);
    }
};

// --- Multi-select cancel (checkboxes -> one dialog -> one consolidated email) ---
// Only saved, still-active items can be bulk-canceled; staged/unsaved rows and
// already-canceled items are not selectable.
const bulkSelectedIds = ref([]);

const selectableItemIds = computed(() =>
    form.sites
        .map((row) => savedItemFor(row))
        .filter((it) => it && !it.canceled_at)
        .map((it) => it.id)
);

const allActiveSelected = computed(() =>
    selectableItemIds.value.length > 0 &&
    bulkSelectedIds.value.length === selectableItemIds.value.length
);

const toggleSelectAll = () => {
    bulkSelectedIds.value = allActiveSelected.value ? [] : [...selectableItemIds.value];
};

const openBulkCancel = () => {
    if (!bulkSelectedIds.value.length) return;
    itemActions.openBulkCancel(bulkSelectedIds.value);
};

const formatDate = (date) => {
    if(!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const form = useForm({
    client_id: order.client_id,
    type_id: order.type_id || 1, 
    payment_date: order.payment_date ? new Date(order.payment_date) : null,
    deferred_payment: order.deferred_payment || false,
    notify_seller: order.notify_seller !== undefined ? order.notify_seller : true,
    notify_client: order.notify_client !== undefined ? order.notify_client : true,
    sites: (() => {
        const items = order.orderItems || order.order_items || [];
        return items.map((item) => {
            const siteData = sites ? sites[item.site?.url] : null;
            
            return {
                id: item.site?.id || item.site_id,
                url: item.site?.url || '',
                link_type: siteData?.link_type || item.site?.link_type || 'DOFOLLOW',
                type_observation: siteData?.type_observation || item.site?.type_observation || '',
                types: siteData?.types || siteData?.offers || item.site?.types || [],
                sellers: siteData?.sellers || item.site?.sellers || [],
                menu: siteData?.menu || item.site?.menu || false,
                banner: siteData?.banner || item.site?.banner || false,
                promo: siteData?.promo || item.site?.promo || false,
                google_news: siteData?.google_news || item.site?.google_news || false,
                embed: siteData?.embed || item.site?.embed || false,
                restrict: siteData?.restrict || item.site?.restrict || false,
                restrict_detail: siteData?.restrict_detail || item.site?.restrict_detail || '',
                archive_article: siteData?.archive_article || item.site?.archive_article || false,
                archive_due: siteData?.archive_due || item.site?.archive_due || '',
                images: siteData?.images || item.site?.images || false,
                pivot_id: item.id,
                site_id: item.site_id,
                seller_id: item.seller?.id,
                seller: item.seller,
                type_id: item.type_id || null, 
                link: item.link,
                observations: item.observations || '',
                cost: item.cost || 0,
                sale: item.sale || 0,
                payment_tax: item.payment_tax || 0,
                cost_coin: item.cost_coin || 'USD',
                sale_coin: item.sale_coin || 'USD',
                comission_coin: item.comission_coin || 'USD',
                received: item.received || false,
                paid: item.paid || false,
                comissioned: item.comissioned || false,
                link_status: item.link_status,
            };
        });
    })(),
});

const DRAFT_ORDER_KEY = 'draft_order_admin_edit';

const loadDraftOrder = () => {
    return;
};

const saveDraftOrder = () => {
    return;
};

const clearDraftOrder = () => {
    localStorage.removeItem(DRAFT_ORDER_KEY);
};

onMounted(() => {
});

watch(() => form.deferred_payment, (newValue) => {
    if (newValue) {
        form.payment_date = null;
    }
});

const submit = () => {
    if (isCanceled.value) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('This order was canceled and can no longer be edited.'),
            life: 4000
        });
        return;
    }

    if (!form.client_id) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a client'),
            life: 3000
        });
        return;
    }
    
    const clientExists = clientOptions.value.some(client => client.value === form.client_id);
    if (!clientExists) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Selected client does not exist'),
            life: 3000
        });
        return;
    }
    
    if (!form.client_id) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a client'),
            life: 3000
        });
        return;
    }
    
    if (!isFormValid.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a type for all sites'),
            life: 3000
        });
        return;
    }
    
    form.transform((data) => {
    const transformedData = {
        status: order.status, 
        client_id: data.client_id,
        type_id: data.type_id || order.type_id || 1,
        notify_seller: data.notify_seller,
        notify_client: data.notify_client,
        items: data.sites.map((item, index) => {
            return {
                id: item.pivot_id, 
                site_id: item.site_id || item.id,
                seller_id: item.seller_id || item.seller?.id,
                type_id: item.type_id,
                link: item.link,
                observations: item.observations || '',
                cost: item.cost || 0,
                sale: item.sale || 0,
                payment_tax: item.payment_tax || 0,
                cost_coin: item.cost_coin || coinStore.coin,
                sale_coin: item.sale_coin || coinStore.coin,
                comission_coin: item.comission_coin || coinStore.coin,
                received: item.received || false,
                paid: item.paid || false,
                comissioned: item.comissioned || false,
                link_status: item.link_status && ['SUBMITTED', 'PRODUCTION', 'WAITING', 'PUBLISHED'].includes(item.link_status) ? item.link_status : null,
                content: itemContents[index] ? {
                    ...itemContents[index],
                    sent_to_portal_at: itemContents[index].sent_to_portal_at || (itemContents[index].publication_date ? formatDate(itemContents[index].publication_date) : null)
                } : null
            };
        }),
        payment_date: data.payment_date ? formatDate(data.payment_date) : null,
        deferred_payment: data.deferred_payment,
    };
    
    return transformedData;
    }).put(route('orders.update', order.id), {
        onStart: () => showLoading($t('Updating order...')),
        onFinish: () => hideLoading(),
        onError(errors) {
            console.error('Order update errors:', errors);
            let errorMessage = $t('Failed to update order');
            
            if (errors.message) {
                errorMessage = errors.message;
            } else if (typeof errors === 'object') {
                const firstKey = Object.keys(errors)[0];
                if (firstKey && errors[firstKey]) {
                    const firstError = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];
                    errorMessage = firstError || errorMessage;
                }
            }
            
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: errorMessage,
                life: 5000 
            });
        },
        onSuccess() {
            clearDraftOrder();
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Order updated successfully'), life: 3000 });
        }
    });
};

const listForm = useForm({
    list: '',
});

const list = ref({});



const normalizeUrl = (url) => {
    try {
        if (!url || !url.trim()) {
            return '';
        }
        
        let cleanUrl = url.trim();
        
        cleanUrl = cleanUrl.replace(/^https?:\/\//i, '');
        
        cleanUrl = cleanUrl.replace(/^www\./i, '');
        
        cleanUrl = cleanUrl.replace(/\/+$/, '');
        
        const pathIndex = cleanUrl.indexOf('/');
        if (pathIndex !== -1) {
            cleanUrl = cleanUrl.substring(0, pathIndex);
        }
        
        const queryIndex = cleanUrl.indexOf('?');
        if (queryIndex !== -1) {
            cleanUrl = cleanUrl.substring(0, queryIndex);
        }
        
        return cleanUrl.toLowerCase();
    } catch (e) {
        console.error('Error normalizing URL:', e);
        return url.trim();
    }
};

const go = () => {
    if (!listForm.list || !listForm.list.trim()) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please enter at least one site URL'), life: 3000 });
        return;
    }

    let processedList = listForm.list
        .split('\n')
        .map(url => normalizeUrl(url))
        .filter(url => url.length > 0)
        .join('\n');

    const originalList = listForm.list;
    listForm.list = processedList;

    listForm.transform(() => ({
        list: processedList
    })).post(route('orders.go'), {
        preserveScroll: true,
        onStart: () => showLoading($t('Processing order list...')),
        onFinish: () => hideLoading(),
        onSuccess: (res) => {
            openOrderDialog.value = false;
            
            list.value = {};

            if (res.props && res.props.sites) {
                const _sites = res.props.sites;
                
                if (_sites && Object.keys(_sites).length > 0) {
                    Object.keys(_sites).map(url => {
                        const site = _sites[url];

                        if (site.offers.length === 0) {
                            site.offer = null;
                            list.value[url] = site;
                        } else {
                            site.offer = site.selected_offer || site.offers[0];
                            list.value[url] = site;
                        }
                    });
                    
                    openSitesDialog.value = true;
                } else {
                    toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('No valid sites found in the list'), life: 3000 });
                }
            } else {
                toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Unexpected response from server'), life: 3000 });
            }
        },
        onError: (errors) => {
            listForm.list = originalList;
            
            let errorMessage = $t('Failed to process site list');
            if (errors.message) {
                errorMessage = errors.message;
            } else if (errors.list) {
                errorMessage = errors.list[0] || errorMessage;
            } else if (typeof errors === 'object') {
                const firstError = Object.values(errors)[0];
                if (Array.isArray(firstError)) {
                    errorMessage = firstError[0];
                } else if (typeof firstError === 'string') {
                    errorMessage = firstError;
                }
            }
            
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: errorMessage,
                life: 5000 
            });
        }
    });
};

const openOrderDialog = ref(false);
const openSitesDialog = ref(false);
let editingValue = ref('');

const add = (site = null, index) => {
    if (site) {
        const newSite = structuredClone(toRaw(site));
        newSite.link = null;
        newSite.observations = '';
        newSite.received = false;
        newSite.paid = false;
        newSite.comissioned = false;
        newSite.link_status = null;
        newSite.types = newSite.types || [];
        newSite.sellers = newSite.sellers || [];

        if (newSite.offer) {
            newSite.cost = 0;
            newSite.cost_coin = 'USD';
            newSite.seller = newSite.offer.seller;
            newSite.seller_id = newSite.offer.seller_id;
            newSite.type_id = null; 
            newSite.sale = 0; 
            newSite.sale_coin = 'USD';
            newSite.payment_tax = 0;
        } else {
            newSite.cost = 0;
            newSite.sale = 0;
            newSite.cost_coin = 'USD';
            newSite.sale_coin = 'USD';
            newSite.seller_id = null;
            newSite.type_id = null;
            newSite.payment_tax = 0;
        }
        
        form.sites.splice(index, 0, newSite);
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Site duplicated successfully'), life: 2000 });
        return;
    }

    const sitesAdded = Object.keys(list.value).length;
    if (sitesAdded === 0) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('No sites available to add'), life: 3000 });
        return;
    }
    
    for (let key of Object.keys(list.value)) {
        const siteData = structuredClone(toRaw(list.value[key]));

        siteData.url = siteData.originalUrl || siteData.url;
        
        siteData.link = null;
        siteData.observations = '';
        siteData.received = false;
        siteData.paid = false;
        siteData.comissioned = false;
        siteData.link_status = null;
        siteData.types = siteData.types || [];
        siteData.sellers = siteData.sellers || [];
        siteData.link_type = siteData.link_type || 'DOFOLLOW';
        siteData.type_observation = siteData.type_observation || '';

        if (siteData.offer) {
            siteData.cost = 0;
            siteData.cost_coin = 'USD';
            siteData.seller = siteData.offer.seller;
            siteData.seller_id = siteData.offer.seller_id;
            siteData.type_id = null; 
            siteData.sale = 0; 
            siteData.sale_coin = 'USD';
            siteData.payment_tax = 0;
        } else {
            siteData.cost = 0;
            siteData.sale = 0;
            siteData.cost_coin = 'USD';
            siteData.sale_coin = 'USD';
            siteData.seller_id = null;
            siteData.type_id = null;
            siteData.payment_tax = 0;
        }
        
        form.sites.push(siteData);
    }

    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('{{count}} site(s) added to order', { count: sitesAdded }), life: 3000 });
    openSitesDialog.value = false;

    list.value = {};
    listForm.list = '';
};

const remove = (index) => {
    form.sites.splice(index, 1);
    toast.add({ severity: 'info', summary: $t('Info'), detail: $t('Site removed from order'), life: 2000 });
};

const addLink = (row) => {
    editingRow.value = row;
    linkValue.value = form.sites[editingRow.value].link;
    openLinkDialog.value = true;
};

const addLinkConfirm = () => {
    form.sites[editingRow.value].link = linkValue.value.replace(/^https?:\/\//, '');
    openLinkDialog.value = false;
    linkValue.value = '';
};

const edit = (column, row, site) => {
    editingColumn.value = column;
    editingRow.value = row;
    editingSite.value = site;

    editingCurrency.value = currentCurrency.value;
    
    const originalCurrency = site[`${column}_coin`] || 'USD';
    const originalValue = site[column] || 0;
    editingValue.value = convertPrice(originalValue, originalCurrency, currentCurrency.value);

    openEditDialog.value = true;
};



const editOrderPaymentTax = () => {
    if (form.sites.length === 0) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Add sites first to edit payment tax'), life: 3000 });
        return;
    }
    
    editingColumn.value = 'payment_tax';
    editingRow.value = null; 
    editingSite.value = null;
    editingCurrency.value = currentCurrency.value;

    let total = 0;
    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(paymentTax, currency, currentCurrency.value);
    }
    
    editingValue.value = total;
    
    openEditDialog.value = true;
};

const update = (all) => {
    if (!editingValue.value || editingValue.value < 0) return;

    const column = editingColumn.value;

    if (column === 'payment_tax' && editingRow.value === null) {
        const totalValue = editingValue.value;
        const numSites = form.sites.length;
        
        if (numSites > 0) {
            const valuePerSite = totalValue / numSites;
            
            form.sites.forEach(site => {
                const siteCurrency = site.sale_coin || 'USD';
                const convertedValue = convertPrice(valuePerSite * 100, currentCurrency.value, siteCurrency);
                site.payment_tax = Math.floor(convertedValue * 100);
            });
        }
    }
    else if (all) {
        form.sites
            .filter(site => site.url === editingSite.value.url)
            .forEach(site => {
                const siteCurrency = site[`${column}_coin`] || 'USD';
                const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
                site[column] = Math.floor(convertedValue * 100);
            });
    } else {
        const site = form.sites[editingRow.value];
        const siteCurrency = site[`${column}_coin`] || 'USD';
        const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
        site[column] = Math.floor(convertedValue * 100);
    }

    openEditDialog.value = false;
    editingValue.value = '';
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Values updated successfully'), life: 2000 });
};

const showEditAll = computed(() => {
    if (editingSite.value === null || editingColumn.value === 'payment_tax') {
        return false;
    }
    return form.sites.filter(site => site.url === editingSite.value.url).length > 1;
});

const clientOptions = computed(() => {
    return clients.map(client => {
        const fullName = [client.first_name, client.last_name]
            .filter(Boolean)
            .join(' ')
            .trim();

        return {
            label: fullName || client.name,
            value: client.id
        };
    });
});

const filteredClients = ref([]);
const selectedClient = ref(null);

watch(() => form.client_id, (newClientId) => {
    if (newClientId && !selectedClient.value) {
        const client = clientOptions.value.find(c => c.value === newClientId);
        if (client) {
            selectedClient.value = client;
        }
    }
}, { immediate: true });

const searchClients = (event) => {
    const query = event.query.toLowerCase();
    filteredClients.value = clientOptions.value.filter(client => 
        client.label.toLowerCase().includes(query)
    );
};

const onClientSelect = (event) => {
    const client = event.value;
    if (client && client.value) {
        form.client_id = client.value;
        selectedClient.value = client;
    }
};

const onClientClear = () => {
    form.client_id = null;
    selectedClient.value = null;
};

const getTypeOptions = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (!site.types) return [];

    const uniqueTypes = new Map();
    
    site.types.forEach(type => {
        if (!uniqueTypes.has(type.id)) {
            const priceDisplay = formatConvertedPrice(
                type.converted_price, 
                type.converted_coin
            );
            
            uniqueTypes.set(type.id, {
                label: `${type.name} - ${priceDisplay}`,
                value: type.id
            });
        }
    });
    
    return Array.from(uniqueTypes.values());
};

const toggleContentItemExpansion = (itemIndex) => {
    expandedContentItems.value[itemIndex] = !expandedContentItems.value[itemIndex];
};

const onPublicationDateChanged = ({ contentId, date }) => {
    const itemIndex = contentId;
    
    if (!itemContents[itemIndex]) {
        itemContents[itemIndex] = {
            id: itemIndex,
            confirmed_by_admin_at: new Date().toISOString()
        };
    }
    
    itemContents[itemIndex].sent_to_portal_at = date;
    itemContents[itemIndex].publication_date = date;
    
    Object.assign(itemContents, { ...itemContents });
};

const itemsWithContent = computed(() => {
    return form.sites.map((site, index) => {
        const selectedType = site.types?.find(t => t.id === site.type_id);
        
        return {
            id: index,
            site_url: site.url,
            type_name: selectedType?.name || '-',
            site: site,
            observations: site.observations || '',
            site_features: {
                accepted_anchors: selectedType?.accepted_anchors || 1,
                menu: site.menu || false,
                banner: site.banner || false,
                promo: site.promo || false,
                google_news: site.google_news || false,
                embed: site.embed || false,
                restrict: site.restrict || false,
                restrict_detail: site.restrict_detail || '',
                archive_article: site.archive_article || false,
                archive_due: site.archive_due || '',
                images: site.images || false,
            },
            content: itemContents[index] ? {
                ...itemContents[index],
                sent_to_portal_at: itemContents[index].sent_to_portal_at
            } : null
        };
    });
});

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';

    const normalizedType = linkType.toUpperCase().trim();

    if (normalizedType.includes('DOFOLLOW')) {
        return 'DOFOLLOW';
    }

    if (normalizedType.includes('SPONSOR')) {
        return 'SPONSORED';
    }

    return 'NOFOLLOW';
};

const maxAnchorsComputed = computed(() => {
    if (selectedItems.value.length === 0) return 1;
    return Math.max(...selectedItems.value.map(item => {
        const selectedType = item.types?.find(t => t.id === item.type_id);
        return selectedType?.accepted_anchors || 1; 
    }));
});

const onContentSubmit = async (contentData) => {
    if (currentItemIndex.value !== null) {
        const existingContent = itemContents[currentItemIndex.value];
        itemContents[currentItemIndex.value] = {
            ...contentData,
            sent_to_portal_at: existingContent?.sent_to_portal_at || contentData.sent_to_portal_at,
            skipped_validation: contentData.skipped === true,
        };
        
        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Content saved successfully'),
            life: 3000
        });
        
        closeContentModal();
    }
};

onMounted(() => {
    if (form.client_id) {
        const client = clientOptions.value.find(c => c.value === form.client_id);
        if (client) {
            selectedClient.value = client;
        }
    }

    if (order.orderItems || order.order_items) {
        const items = order.orderItems || order.order_items;
        items.forEach((item, index) => {
            if (item.content && Object.keys(item.content).length > 0) {
                const contentData = {
                    id: index,
                    content_link: item.content.content_link || '',
                    content_link_file: item.content.content_link_file || null,
                    language: item.content.language || 'en',
                    request_suggested_article: item.content.request_suggested_article || false,
                    auto_content: item.content.auto_content || 0,
                    skipped_validation: item.content.skipped_validation || false,
                    publication_date: item.content.sent_to_portal_at ? new Date(item.content.sent_to_portal_at) : null,
                    sent_to_portal_at: item.content.sent_to_portal_at,
                    confirmed_by_admin_at: item.content.confirmed_by_admin_at || new Date().toISOString(),
                };

                if (item.content.anchor_name && item.content.anchor_url) {
                    contentData.anchor_name = item.content.anchor_name;
                    contentData.anchor_url = item.content.anchor_url;
                }

                if (item.content.anchor_name_2 && item.content.anchor_url_2) {
                    contentData.anchor_name_2 = item.content.anchor_name_2;
                    contentData.anchor_url_2 = item.content.anchor_url_2;
                }

                if (item.content.anchor_name_3 && item.content.anchor_url_3) {
                    contentData.anchor_name_3 = item.content.anchor_name_3;
                    contentData.anchor_url_3 = item.content.anchor_url_3;
                }

                itemContents[index] = contentData;
            }
        });
    }
});

const formatCurrency = (value, currencyCode) => {
    const currency = currencyCode || coinStore.coin;
    return formatWithSymbol(value, currency);
};

const currencyOptions = computed(() => {
    return Object.keys(coins).map(currency => ({
        label: currency,
        value: currency
    }));
});

const openContentModal = (itemIndex) => {
    const item = form.sites[itemIndex];
    
    currentItemIndex.value = itemIndex;
    selectedItems.value = [item];

    siteFeatures.value = {
        menu: item.menu || false,
        banner: item.banner || false,
        promo: item.promo || false,
        google_news: item.google_news || false,
        embed: item.embed || false,
        restrict: item.restrict || false,
        restrict_detail: item.restrict_detail || '',
        archive_article: item.archive_article || false,
        archive_due: item.archive_due || '',
        images: item.images || false,
    };

    const selectedType = item.types?.find(t => t.id === item.type_id);
    const acceptedAnchors = selectedType?.accepted_anchors || 1;
    
    selectedOrderItem.value = {
        accepted_anchors: acceptedAnchors,
        url: item.url,
        type_name: selectedType?.name
    };
    
    showContentModal.value = true;
};

const closeContentModal = () => {
    showContentModal.value = false;
    selectedItems.value = [];
    siteFeatures.value = null;
    selectedOrderItem.value = null;
    currentItemIndex.value = null;
};

const addSites = () => {
    openOrderDialog.value = true;
};

onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});

const onTypeChange = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (site.type_id) {
        const selectedOffer = site.types?.find(offer => offer.id === site.type_id);
        
        if (selectedOffer) {
            site.cost = selectedOffer.cost || 0;
            site.cost_coin = selectedOffer.cost_coin || 'USD';
            site.sale = selectedOffer.converted_price;
            site.sale_coin = selectedOffer.converted_coin;
            site.seller_id = selectedOffer.seller_id;
            site.seller = selectedOffer.seller;
            
            const taxPercentage = parseFloat(payment_settings?.payment_processing_tax || 0);
            site.payment_tax = Math.floor((site.sale || 0) * (taxPercentage / 100));
        }
    } else {
        site.cost = 0;
        site.sale = 0;
        site.seller_id = null;
        site.seller = null;
        site.payment_tax = 0;
    }
};

const isFormValid = computed(() => {
    return form.sites.every(site => {
        return site.type_id && site.type_id !== null && site.type_id !== undefined;
    });
});

const isPaymentConfirmed = computed(() => {
    return form.deferred_payment || (form.payment_date !== null && form.payment_date !== '');
});

const isPublicationReady = computed(() => {
    if (form.sites.length === 0) return false;
    return form.sites.every((site, index) => 
        itemContents[index] && 
        (itemContents[index].sent_to_portal_at || itemContents[index].publication_date)
    );
});

const isAllStepsComplete = computed(() => {
    if (form.sites.length === 0) return false;
    if (!form.client_id) return false; 
    return isFormValid.value && isPaymentConfirmed.value && isPublicationReady.value;
});

const allContentSentToPortal = computed(() => {
    if (form.sites.length === 0) return false;
    
    return form.sites.every((site, index) => {
        const content = itemContents[index];
        return content && 
               (content.sent_to_portal_at !== null &&
                content.sent_to_portal_at !== undefined);
    });
});

const hasConfirmedContent = computed(() => {
    return form.sites.some((site, index) => {
        const content = itemContents[index];
        return content && (
            content.confirmed_by_admin_at || 
            content.sent_to_portal_at ||
            Object.keys(content).length > 0
        );
    });
});

const contentReviewItems = computed(() => {
    const currentOrder = proxy.$props.order;
    const items = currentOrder?.orderItems || currentOrder?.order_items || [];
    return items
        .filter(item => item.content)
        .map(item => ({
            id: item.id,
            site_url: item.site?.url || '',
            type_name: item.type?.name || '-',
            observations: item.observations || '',
            site_features: item.site ? {
                menu: item.site.menu || false,
                banner: item.site.banner || false,
                promo: item.site.promo || false,
                google_news: item.site.google_news || false,
                embed: item.site.embed || false,
                restrict: item.site.restrict || false,
                restrict_detail: item.site.restrict_detail || '',
                archive_article: item.site.archive_article || false,
                archive_due: item.site.archive_due || '',
                images: item.site.images || false,
                accepted_anchors: item.accepted_anchors || 1,
            } : null,
            content: {
                id: item.content.id,
                anchor_name: item.content.anchor_name,
                anchor_url: item.content.anchor_url,
                anchor_name_2: item.content.anchor_name_2,
                anchor_url_2: item.content.anchor_url_2,
                anchor_name_3: item.content.anchor_name_3,
                anchor_url_3: item.content.anchor_url_3,
                language: item.content.language,
                content_link: item.content.content_link,
                content_link_file: item.content.content_link_file,
                auto_content: item.content.auto_content,
                submited_at: item.content.submited_at,
                confirmed_by_admin_at: item.content.confirmed_by_admin_at,
                rejected_at: item.content.rejected_at,
                rejection_reason: item.content.rejection_reason,
                revised_at: item.content.revised_at,
                sent_to_client_approval_at: item.content.sent_to_client_approval_at,
                client_approved_at: item.content.client_approved_at,
                client_rejected_at: item.content.client_rejected_at,
                client_rejection_reason: item.content.client_rejection_reason,
                sent_to_portal_at: item.content.sent_to_portal_at,
                review_logs: item.content.review_logs ?? [],
            },
        }));
});

const allContentReviewConfirmed = computed(() => {
    if (!contentReviewItems.value.length) return false;
    return contentReviewItems.value.every(i => i.content?.confirmed_by_admin_at);
});

const hasContentToReview = computed(() => contentReviewItems.value.length > 0);

const hasAutoContent = computed(() => {
    return contentReviewItems.value.some(item => item.content?.auto_content === 1);
});

const isArticleApprovalEnabled = computed(() => {
    return allContentReviewConfirmed.value;
});

const allClientApproved = computed(() => {
    if (!hasAutoContent.value) return true;
    return contentReviewItems.value
        .filter(item => item.content?.auto_content === 1)
        .every(item => item.content?.client_approved_at);
});

const linkStatusOptions = computed(() => [
    { label: $t('Enviado ao portal'), value: 'SUBMITTED' },
    { label: $t('Em redação'), value: 'PRODUCTION' },
    { label: $t('Aguardando aprovação'), value: 'WAITING' },
    { label: $t('Publicado'), value: 'PUBLISHED' }
]);

const statusOptions = computed(() => {
    return Object.entries(statuses).map(([key, label]) => ({
        label: label,
        value: key
    }));
});

const typeOptions = computed(() => {
    return types.map(type => ({
        label: type.name,
        value: type.id
    }));
});

const costTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        if (site.type_id && site.cost) {
            const currency = site.cost_coin || 'USD';
            total += convertPrice(site.cost, currency, currentCurrency.value);
        }
    }

    return formatWithSymbolRaw(total, currentCurrency.value);
});

const saleTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        if (site.sale) {
            const currency = site.sale_coin || 'USD';
            total += convertPrice(site.sale, currency, currentCurrency.value);
        }
    }

    return formatWithSymbolRaw(total, currentCurrency.value);
});

const paymentTaxTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(paymentTax, currency, currentCurrency.value);
    }

    return formatWithSymbolRaw(total, currentCurrency.value);
});

const paymentTaxPercentage = computed(() => {
    return parseFloat(payment_settings?.payment_processing_tax || 0);
});

const grandTotal = computed(() => {
    let total = 0;
    
    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const saleValue = site.sale || 0;
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(saleValue + paymentTax, currency, currentCurrency.value);
    }
    
    return formatWithSymbol(total, currentCurrency.value);
});

</script>

<template>

    <!-- Toast is provided globally by AdminMainLayout; a second <Toast> here would
         render every message twice (PrimeVue broadcasts to all mounted Toasts). -->

    <!-- Add Sites Dialog -->
    <Dialog v-model:visible="openOrderDialog" :header="$t('Add sites')" :modal="true" :style="{ width: '50vw' }">
        <div class="field">
            <label for="list" class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Site list') }}</label>
            <Textarea
                id="list"
                v-model="listForm.list"
                rows="5"
                class="w-full"
                :placeholder="$t('Enter site URLs, one per line')"
            />
            <small v-if="listForm.errors.list" class="p-error">{{ listForm.errors.list }}</small>
        </div>

        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openOrderDialog = false" />
            <Button :label="$t('Go')" icon="pi pi-check" @click="go" :disabled="listForm.processing" />
        </template>
    </Dialog>

    <!-- Sites Selection Dialog -->
    <Dialog v-model:visible="openSitesDialog" :header="$t('Add sites')" :modal="true" :style="{ width: '70vw' }">
        <DataTable :value="Object.entries(list).map(([url, site]) => ({url, ...site}))"
                   responsiveLayout="scroll"
                   class="p-datatable-sm add-sites-table">
            <Column field="url" :header="$t('Portal')" style="min-width:280px" />
            <Column field="offer.seller.name" :header="$t('Vendedor')">
                <template #body="slotProps">
                    {{ slotProps.data.offer?.seller?.name ?? '-' }}
                </template>
            </Column>
            <Column :header="$t('Valor de compra')">
                <template #body="slotProps">
                    <span v-if="slotProps.data.offer === null">-</span>
                    <span v-else>
                        {{ slotProps.data.offer.cost_coin != coinStore.coin ? '~ ' : '' }}
                        {{ formatCurrency(Math.round((slotProps.data.offer.cost / coinStore.ratios[slotProps.data.offer.cost_coin]) / 100 * 100) / 100, slotProps.data.offer.cost_coin) }}
                    </span>
                </template>
            </Column>
            <Column :header="$t('Valor de venda')">
                <template #body="slotProps">
                    <span v-if="slotProps.data.offer === null">-</span>
                    <span v-else>
                        <!-- Prioridade: calculated_price > sale normal -->
                        <div v-if="slotProps.data.offer.calculated_price && slotProps.data.offer.calculated_price > 0">
                            {{ formatCurrency(Math.round((slotProps.data.offer.calculated_price / coinStore.ratios[slotProps.data.offer.cost_coin]) / 100 * 100) / 100, slotProps.data.offer.cost_coin) }}
                        </div>
                        <div v-else-if="slotProps.data.offer.sale && slotProps.data.offer.sale > 0">
                            {{ slotProps.data.offer.sale_coin != coinStore.coin ? '~ ' : '' }}
                            {{ formatCurrency(Math.round((slotProps.data.offer.sale / coinStore.ratios[slotProps.data.offer.sale_coin]) / 100 * 100) / 100, slotProps.data.offer.sale_coin) }}
                        </div>
                        <div v-else>-</div>
                    </span>
                </template>
            </Column>
        </DataTable>

        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openSitesDialog = false" />
            <Button :label="$t('Add to order')" icon="pi pi-check" @click="add(null)" />
        </template>
    </Dialog>

    <!-- Edit Value Dialog -->
    <Dialog v-model:visible="openEditDialog" :modal="true" :style="{ width: '35vw' }">
        <template #header>
            <span v-if="editingColumn === 'cost'">{{ $t('Edit purchase value') }}</span>
            <span v-if="editingColumn === 'sale'">{{ $t('Edit sale value') }}</span>
            <span v-if="editingColumn === 'payment_tax'">{{ $t('Edit payment tax') }}</span>
        </template>

        <div class="flex items-center gap-4">
            <div class="w-full">
                <div class="field">
                    <label for="value">{{ $t('Value') }}</label>
                    <InputNumber
                        id="value"
                        v-model="editingValue"
                        :minFractionDigits="0"
                        :maxFractionDigits="2"
                        class="w-full"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <Button :label="$t('Back')" icon="pi pi-times" text @click="openEditDialog = false" />
            <Button :label="$t('Update')" icon="pi pi-check" @click="update(false)" />
            <Button v-if="showEditAll" :label="$t('Update all')" icon="pi pi-check-circle" @click="update(true)" severity="success" />
        </template>
    </Dialog>

    <!-- Add Link Dialog -->
    <Dialog v-model:visible="openLinkDialog" :header="$t('Adicionar link')" :modal="true" :style="{ width: '30vw' }">
        <div class="field">
            <label for="link" class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Link') }}</label>
            <InputText
                id="link"
                v-model="linkValue"
                class="w-full"
            />
        </div>

        <template #footer>
            <Button :label="$t('Back')" icon="pi pi-times" text @click="openLinkDialog = false" />
            <Button :label="$t('Add')" icon="pi pi-check" @click="addLinkConfirm" />
        </template>
    </Dialog>

    <!-- Main Form -->
    <div class="grid">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex align-items-center justify-between mb-6">
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-pencil text-2xl"></i>
                            <span class="flex items-center">{{ $t('Edit order') }}</span>
                        </div>
                        <Button
                            :label="$t('Back to Orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="$inertia.get(route('orders.index'))"
                        />
                    </div>
                </template>

                <template #content>
                    <form @submit.prevent="submit">
                        <Message v-if="isCanceled" severity="error" :closable="false" class="mb-6">
                            {{ $t('This order was canceled and can no longer be edited.') }}
                        </Message>
                        <fieldset :disabled="isCanceled" :class="{ 'opacity-70': isCanceled }" class="border-0 p-0 m-0">
                        <div class="grid gap-10">
                            <!-- Client -->
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label class="block text-sm font-medium text-surface-700 mb-2">
                                        {{ $t('Client') }} <span class="text-red-500">*</span>
                                    </label>
                                    <AutoComplete
                                        v-model="selectedClient"
                                        :suggestions="filteredClients"
                                        @complete="searchClients"
                                        @item-select="onClientSelect"
                                        @clear="onClientClear"
                                        optionLabel="label"
                                        :placeholder="$t('Search for a client...')"
                                        class="w-full"
                                        :class="{ 'p-invalid': !form.client_id && form.sites.length > 0 }"
                                        :minLength="1"
                                        :dropdown="true"
                                        :forceSelection="false"
                                    />
                                    <small v-if="form.errors.client_id" class="p-error">{{ form.errors.client_id }}</small>
                                </div>
                            </div>

                            <!-- Sites Table and Summary -->
                            <div class="col-12">
                                <div class="field">
                                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                        <!-- Sites Table -->
                                        <!-- min-w-0 lets this grid column shrink so the scrollable table
                                             scrolls internally instead of pushing the page past the viewport. -->
                                        <div class="lg:col-span-3 min-w-0">
                                            <Card>
                                                <template #title>
                                                    <div class="flex items-center justify-between">
                                                        <span>{{ $t('Order Sites') }}</span>
                                                        <span class="text-sm font-normal text-surface-600">{{ form.sites?.length || 0 }} {{ (form.sites?.length || 0) === 1 ? $t('site') : $t('sites') }}</span>
                                                    </div>
                                                </template>
                                                <template #content>
                                    <DataTable
                                        :value="form.sites"
                                        responsiveLayout="scroll"
                                        class="p-datatable-sm order-items-table"
                                        :scrollable="true"
                                        scrollHeight="400px"
                                        :rowClass="rowClass"
                                    >
                                        <template #empty>
                                            <div class="text-center p-4">
                                                {{ $t('No site have been added to this order yet.') }}
                                            </div>
                                        </template>

                                        <!-- Multi-select checkbox (saved, active items only) for bulk cancel -->
                                        <Column headerStyle="width: 3rem" style="width: 3rem" bodyStyle="width: 3rem; white-space: nowrap;">
                                            <template #header>
                                                <Checkbox
                                                    v-if="canEditItems(order) && selectableItemIds.length"
                                                    :binary="true"
                                                    :modelValue="allActiveSelected"
                                                    @update:modelValue="toggleSelectAll"
                                                    v-tooltip.top="$t('Select all')"
                                                />
                                            </template>
                                            <template #body="slotProps">
                                                <Checkbox
                                                    v-if="canEditItems(order) && savedItemFor(slotProps.data) && !savedItemFor(slotProps.data)?.canceled_at"
                                                    v-model="bulkSelectedIds"
                                                    :value="savedItemFor(slotProps.data).id"
                                                />
                                            </template>
                                        </Column>

                                        <Column :header="$t('Actions')" style="width: 90px; white-space: nowrap;" headerStyle="width: 90px" bodyStyle="white-space: nowrap;">
                                            <template #body="slotProps">
                                                <div class="flex flex-wrap items-center gap-2">
                                                    <!-- Already-canceled saved items: marker only, no action -->
                                                    <Tag
                                                        v-if="savedItemFor(slotProps.data)?.canceled_at"
                                                        :value="$t('Canceled')"
                                                        severity="danger"
                                                    />
                                                    <!-- Single remove affordance: soft-cancel (saved) or splice (staged) -->
                                                    <span
                                                        v-else
                                                        v-tooltip.top="!canEditItems(order) ? $t('Order finalized — items can no longer be edited') : ''"
                                                    >
                                                        <Button
                                                            icon="pi pi-trash"
                                                            severity="danger"
                                                            outlined
                                                            size="small"
                                                            :disabled="!canEditItems(order)"
                                                            @click="removeItem(slotProps.data, slotProps.index)"
                                                        />
                                                    </span>
                                                </div>
                                            </template>
                                        </Column>

                                        <Column field="url" :header="$t('Portal')" style="min-width: 300px; width: 40%;" headerStyle="min-width: 300px; width: 40%;">
                                            <template #body="slotProps">
                                                <div class="flex align-items-center gap-2">
                                                    <span class="text-sm break-all">{{ slotProps.data.url }}</span>
                                                    <a :href="route('sites.edit', slotProps.data.site_id)" target="_blank" class="text-blue-500">
                                                        <i class="pi pi-external-link text-xs"></i>
                                                    </a>
                                                </div>
                                            </template>
                                        </Column>

                                        <Column :header="$t('Seller')" style="min-width: 120px; white-space: nowrap;" bodyStyle="white-space: nowrap;">
                                            <template #body="slotProps">
                                                <span class="text-sm text-surface-700">
                                                    {{ slotProps.data.seller?.name || '-' }}
                                                </span>
                                            </template>
                                        </Column>

                                        <Column :header="$t('Type')" style="min-width: 200px">
                                            <template #body="slotProps">
                                                <div v-if="!slotProps.data.types || slotProps.data.types.length === 0" class="text-red-500 text-sm">
                                                    {{ $t('No types available') }}
                                                </div>
                                                <Select
                                                    v-else
                                                    v-model="slotProps.data.type_id"
                                                    :options="slotProps.data.types || []"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    :placeholder="$t('Choose a type')"
                                                    class="w-full"
                                                    @change="onTypeChange(slotProps.index)"
                                                >
                                                    <template #option="optionProps">
                                                        <div class="flex items-center justify-between w-full">
                                                            <span>{{ optionProps.option.name }}</span>
                                                            <span class="font-semibold text-primary-600 ml-2">
                                                                {{ formatConvertedPrice(optionProps.option.converted_price, optionProps.option.converted_coin) }}
                                                            </span>
                                                        </div>
                                                    </template>
                                                </Select>
                                                <small v-if="!slotProps.data.type_id" class="text-red-500">{{ $t('Required') }}</small>
                                            </template>
                                        </Column>

                                        <Column :header="$t('Link Type')" style="min-width: 120px">
                                            <template #body="slotProps">
                                                <div class="relative inline-flex items-center justify-center">
                                                    <div class="flex items-center gap-1 px-3 py-2 bg-surface-50 border border-surface-200 rounded-md text-surface-900 text-center">
                                                        {{ formatLinkType(slotProps.data.link_type) }}
                                                        <i
                                                            v-if="slotProps.data.type_observation"
                                                            class="pi pi-info-circle text-blue-500 cursor-pointer"
                                                            v-tooltip.top="slotProps.data.type_observation"
                                                            style="font-size: 0.7rem;"
                                                        ></i>
                                                    </div>
                                                </div>
                                            </template>
                                        </Column>

                                        <Column :header="$t('Observations')" style="min-width: 250px">
                                            <template #body="slotProps">
                                                <Textarea
                                                    v-model="slotProps.data.observations"
                                                    rows="1"
                                                    autoResize
                                                    class="w-full"
                                                    :placeholder="$t('Add notes...')"
                                                />
                                            </template>
                                        </Column>

                                        <Column :header="$t('Purchase Value')" style="min-width: 130px; white-space: nowrap;" bodyStyle="white-space: nowrap;">
                                            <template #body="slotProps">
                                                <span v-if="slotProps.data.cost" class="whitespace-nowrap">
                                                    {{ formatConvertedPrice(slotProps.data.cost, slotProps.data.cost_coin || 'USD') }}
                                                </span>
                                                <span v-else>-</span>
                                            </template>
                                        </Column>

                                        <Column :header="$t('Sale Value')" style="min-width: 150px; white-space: nowrap;" bodyStyle="white-space: nowrap;">
                                            <template #body="slotProps">
                                                <div v-if="slotProps.data.sale" class="flex items-center gap-1 whitespace-nowrap">
                                                    <span>
                                                        {{ formatConvertedPrice(slotProps.data.sale, slotProps.data.sale_coin || 'USD') }}
                                                    </span>
                                                    <Button
                                                        icon="pi pi-pencil"
                                                        text
                                                        rounded
                                                        size="small"
                                                        class="ml-1"
                                                        @click="edit('sale', slotProps.index, slotProps.data)"
                                                    />
                                                </div>
                                                <span v-else>-</span>
                                            </template>
                                        </Column>
                                    </DataTable>

                                    <!-- Single add affordance: routes through the service (proper
                                         pricing, emails client, writes change-log). Disabled + tooltip
                                         when the order is locked (invoice generated / gateway-settled). -->
                                    <span
                                        v-tooltip.top="!canEditItems(order) ? $t('Order finalized — items can no longer be edited') : ''"
                                        class="inline-block mt-4"
                                    >
                                        <Button
                                            :label="$t('Add item')"
                                            icon="pi pi-plus"
                                            :disabled="!canEditItems(order)"
                                            @click="itemActions.openAddItem()"
                                        />
                                    </span>

                                    <!-- Bulk cancel: one dialog, one shared reason, ONE email to the client -->
                                    <Button
                                        v-if="bulkSelectedIds.length"
                                        :label="$t('Cancel selected') + ' (' + bulkSelectedIds.length + ')'"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        class="mt-4 ml-2"
                                        :disabled="!canEditItems(order)"
                                        @click="openBulkCancel"
                                    />
                                </template>
                            </Card>
                        </div>

                        <!-- Order Summary -->
                        <div class="lg:col-span-1">
                            <Card class="sticky top-4">
                                <template #title>
                                    {{ $t('Order Summary') }}
                                </template>
                                <template #content>
                                    <div class="space-y-4">
                                        <div class="flex justify-between items-center">
                                            <span class="text-surface-700">{{ $t('Total Sites') }}:</span>
                                            <span class="font-semibold text-lg">{{ form.sites.length }}</span>
                                        </div>

                        <Divider />

                        <div class="flex justify-between items-center">
                            <span class="text-surface-700">{{ $t('Purchase Value') }}:</span>
                            <span class="font-semibold whitespace-nowrap">{{ costTotal || '--' }}</span>
                        </div>

                        <div class="flex justify-between items-center">
                            <span class="text-surface-700">{{ $t('Sale Value') }}:</span>
                            <span class="font-semibold text-primary-600 whitespace-nowrap"> {{ saleTotal }}</span>
                        </div>                                        <div class="flex justify-between items-center cursor-pointer" @click="editOrderPaymentTax">
                                            <span class="text-surface-700">
                                                {{ $t('Payment Processing Tax') }} ({{ paymentTaxPercentage }}%):
                                            </span>
                                            <div class="flex items-center gap-1">
                                                <span class="font-semibold text-orange-600 whitespace-nowrap"> {{ paymentTaxTotal }}</span>
                                                <Button
                                                    icon="pi pi-pencil"
                                                    text
                                                    rounded
                                                    size="small"
                                                    class="ml-1"
                                                    @click="editOrderPaymentTax"
                                                />
                                            </div>
                                        </div>

                                        <Divider />

                                        <div class="flex justify-between items-center">
                                            <span class="text-xl font-semibold text-surface-900">{{ $t('Total') }}:</span>
                                            <span class="text-xl font-bold text-primary-600 whitespace-nowrap"> {{ grandTotal }}</span>
                                        </div>
                                    </div>
                                </template>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

                            <!-- Payment Confirmation Section -->
                            <div class="col-12">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full flex items-center justify-center"
                                                    :class="isPaymentConfirmed ? 'bg-green-100' : 'bg-amber-100'">
                                                    <span class="font-bold text-lg" 
                                                        :class="isPaymentConfirmed ? 'text-green-700' : 'text-amber-700'">1</span>
                                                </div>
                                                <div>
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Payment Confirmation') }}
                                                    </h2>
                                                    <p class="text-sm font-medium" 
                                                        :class="isPaymentConfirmed ? 'text-green-600' : 'text-amber-600'">
                                                        {{ isPaymentConfirmed ? $t('Payment confirmed') : $t('Configure payment settings for this order') }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <!-- Payment Method Selection -->
                                            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <div class="flex items-start gap-3">
                                                    <Checkbox
                                                        v-model="form.deferred_payment"
                                                        inputId="deferred_payment"
                                                        :binary="true"
                                                    />
                                                    <label for="deferred_payment" class="cursor-pointer">
                                                        <div class="font-medium text-surface-900">{{ $t('Deferred Payment') }}</div>
                                                        <div class="text-sm text-surface-600">{{ $t('When this option is selected, payment will be processed at the end of the purchase.') }}</div>
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- Payment Date Input -->
                                            <div v-if="!form.deferred_payment" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                                <label class="block text-sm font-medium text-surface-700 mb-3">
                                                    {{ $t('Select the Payment Date') }}
                                                    <span class="text-red-500">*</span>
                                                </label>
                                                <Calendar
                                                    v-model="form.payment_date"
                                                    showIcon
                                                    dateFormat="dd/mm/yy"
                                                    class="w-full md:w-[400px]"
                                                    :class="{ 'p-invalid': form.errors.payment_date }"
                                                />
                                                <small v-if="form.errors.payment_date" class="text-red-600 block mt-2">
                                                    {{ form.errors.payment_date }}
                                                </small>
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Content Management Section -->
                            <div class="col-12" v-if="form.sites.length > 0">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full flex items-center justify-center"
                                                    :class="Object.keys(itemContents).length === form.sites.length ? 'bg-green-100' : 'bg-amber-100'">
                                                    <span class="font-bold text-lg" 
                                                        :class="Object.keys(itemContents).length === form.sites.length ? 'text-green-700' : 'text-amber-700'">2</span>
                                                </div>
                                                <div>
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Content Management') }}
                                                    </h2>
                                                    <p class="text-sm font-medium" 
                                                        :class="Object.keys(itemContents).length === form.sites.length ? 'text-green-600' : 'text-amber-600'">
                                                        <span v-if="Object.keys(itemContents).length === 0">{{ $t('No content added yet') }}</span>
                                                        <span v-else-if="Object.keys(itemContents).length === form.sites.length">
                                                            {{ isPublicationReady ? $t('Request sent to site responsible') : $t('Content pending publication') }}
                                                        </span>
                                                        <span v-else>{{ $t('Content progress') }} ({{ Object.keys(itemContents).length }}/{{ form.sites.length }})</span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <!-- Content Items -->
                                            <Card v-for="(site, index) in form.sites" :key="index" class="shadow-sm">
                                                <template #header>
                                                    <div class="p-4 pb-0">
                                                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                                            <div class="flex items-center gap-3">
                                                                <div class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                                    <span class="font-bold text-primary-700">{{ index + 1 }}</span>
                                                                </div>
                                                                <div>
                                                                    <h3 class="font-semibold text-surface-900">
                                                                        {{ site.url }}
                                                                    </h3>
                                                                    <p class="text-sm text-surface-600">
                                                                        {{ site.types?.find(t => t.id === site.type_id)?.name || $t('No type selected') }}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-center gap-3">
                                                                <!-- Canceled marker only; removal is handled by the
                                                                     single trash affordance in the Order Sites table -->
                                                                <Tag
                                                                    v-if="savedItemFor(site)?.canceled_at"
                                                                    :value="$t('Canceled')"
                                                                    severity="danger"
                                                                />
                                                                <Tag v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0"
                                                                     :value="$t('Content Added')" severity="success" icon="pi pi-check" />
                                                                <Tag v-else :value="$t('Pending Content')" severity="warn" icon="pi pi-clock" />
                                                                <Tag
                                                                    v-if="itemContents[index]?.skipped_validation"
                                                                    :value="$t('Sem Validação')"
                                                                    severity="warn"
                                                                    icon="pi pi-exclamation-triangle"
                                                                    v-tooltip.top="$t('Conteúdo salvo sem validação de âncoras')"
                                                                />
                                                                
                                                                <Button
                                                                    :label="itemContents[index] ? $t('Edit Content') : $t('Add Content')"
                                                                    :icon="itemContents[index] ? 'pi pi-pencil' : 'pi pi-plus'"
                                                                    :severity="itemContents[index] ? 'secondary' : 'primary'"
                                                                    :disabled="!site.type_id"
                                                                    size="small"
                                                                    @click="openContentModal(index)"
                                                                />
                                                                
                                                                <Button
                                                                    v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0"
                                                                    :icon="expandedContentItems[index] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                                                    :label="$t('Show')"
                                                                    size="small"
                                                                    severity="secondary"
                                                                    outlined
                                                                    @click="toggleContentItemExpansion(index)"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                                <template #content>
                                                    <div v-show="expandedContentItems[index]" v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0">
                                                        <div class="space-y-4">
                                                            <!-- Site Information Section -->
                                                            <SiteFeaturesInfo :features="site" :accepted-anchors="site.types?.find(t => t.id === site.type_id)?.accepted_anchors || 1" />

                                                            <!-- Anchors Section -->
                                                            <div v-if="itemContents[index].anchors && itemContents[index].anchors.length > 0" 
                                                                 class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                                                <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                                                    <i class="pi pi-link text-blue-700 mr-2"></i>
                                                                    {{ $t('Anchors') }}
                                                                </h5>
                                                                
                                                                <div class="flex flex-col md:flex-row gap-4">
                                                                    <!-- Dynamic Anchors -->
                                                                    <div v-for="(anchor, aIndex) in itemContents[index].anchors.slice(0, 3)" :key="aIndex" class="flex-1">
                                                                        <div class="space-y-2">
                                                                            <div>
                                                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                                                    {{ $t('Anchor Name') }} {{ aIndex + 1 }}
                                                                                </label>
                                                                                <p class="text-sm text-blue-900">{{ anchor.text || '-' }}</p>
                                                                            </div>
                                                                            <div>
                                                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                                                    {{ $t('Anchor URL') }} {{ aIndex + 1 }}
                                                                                </label>
                                                                                <p class="text-xs text-blue-900 break-all">{{ anchor.url || '-' }}</p>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <!-- Divider between anchors -->
                                                                        <Divider v-if="aIndex < Math.min(itemContents[index].anchors.length - 1, 2)" layout="vertical" class="hidden md:flex" />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <!-- Language and Article Title -->
                                                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div v-if="itemContents[index].language">
                                                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                                                        {{ $t('Language') }}
                                                                    </label>
                                                                    <p class="text-surface-900">{{ itemContents[index].language }}</p>
                                                                </div>

                                                                <div v-if="itemContents[index].article_title">
                                                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                                                        {{ $t('Article Title') }}
                                                                    </label>
                                                                    <p class="text-surface-900">{{ itemContents[index].article_title }}</p>
                                                                </div>
                                                            </div>

                                                            <!-- Content Link or File -->
                                                            <div v-if="itemContents[index].content_link || itemContents[index].content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                                                    {{ $t('Content') }}
                                                                </label>

                                                                <div v-if="itemContents[index].content_link" class="flex items-center gap-2">
                                                                    <i class="pi pi-link text-primary-500"></i>
                                                                    <a
                                                                        :href="itemContents[index].content_link"
                                                                        target="_blank"
                                                                        class="text-primary-500 hover:text-primary-600 underline break-all"
                                                                    >
                                                                        {{ itemContents[index].content_link }}
                                                                    </a>
                                                                </div>

                                                                <div v-if="itemContents[index].content_link_file" class="flex items-center gap-2">
                                                                    <i class="pi pi-file text-primary-500"></i>
                                                                    <span class="text-surface-900">{{ $t('File attached') }}</span>
                                                                </div>
                                                            </div>

                                                            <!-- Observations -->
                                                            <div v-if="itemContents[index].observations || site.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                                                <label class="block text-sm font-medium text-amber-900 mb-2">
                                                                    <i class="pi pi-info-circle mr-1"></i>
                                                                    {{ $t('Observations') }}
                                                                </label>
                                                                <p v-if="itemContents[index].observations" class="text-surface-900 mb-2">{{ itemContents[index].observations }}</p>
                                                                <p v-if="site.observations" class="text-surface-900">{{ $t('Site') }}: {{ site.observations }}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Card>
                                            
                                            <small v-if="form.sites.some(site => !site.type_id)" class="text-orange-600">
                                                <i class="pi pi-exclamation-triangle mr-1"></i>
                                                {{ $t('Select a type for all sites to enable content management') }}
                                            </small>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Content Review Section (Admin) -->
                            <div class="col-12" v-if="hasContentToReview">
                                <ContentReviewSection
                                    :order="order"
                                    :items-with-content="contentReviewItems"
                                    :all-confirmed="allContentReviewConfirmed"
                                    :has-content="hasContentToReview"
                                    :is-enabled="true"
                                    @content-confirmed="() => router.reload({ preserveScroll: true })"
                                    @refresh-needed="() => router.reload({ preserveScroll: true })"
                                />
                            </div>

                            <div class="col-12" v-if="hasAutoContent">
                                <ArticleApprovalSection
                                    :order="order"
                                    :items-with-content="contentReviewItems"
                                    :is-enabled="isArticleApprovalEnabled"
                                    :step-number="3"
                                    @refresh-needed="() => router.reload({ preserveScroll: true })"
                                />
                            </div>

                            <!-- Publication Section -->
                            <div class="col-12" v-if="hasConfirmedContent">
                                <PublicationSection
                                    :order="order"
                                    :items-with-content="itemsWithContent"
                                    :all-sent="allContentSentToPortal"
                                    :has-confirmed-content="hasConfirmedContent"
                                    :is-enabled="hasConfirmedContent && (!hasAutoContent || allClientApproved)"
                                    :hide-confirm-button="false"
                                    :is-create-page="false"
                                    :is-edit-mode="true"
                                    @publication-confirmed="() => {}"
                                    @refresh-needed="() => {}"
                                    @publication-date-changed="onPublicationDateChanged"
                                />
                            </div>

                            <!-- Notification Settings -->
                            <div class="col-12" v-if="form.sites.length > 0">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <i class="pi pi-bell text-blue-700 text-lg"></i>
                                                </div>
                                                <div>
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Notification Settings') }}
                                                    </h2>
                                                    <p class="text-sm font-medium text-blue-600">
                                                        {{ $t('Configure who will be notified about order progress') }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <div class="flex items-start gap-3">
                                                <Checkbox
                                                    v-model="form.notify_seller"
                                                    inputId="notify_seller"
                                                    :binary="true"
                                                />
                                                <label for="notify_seller" class="cursor-pointer">
                                                    <div class="font-medium text-surface-900">{{ $t('Notify seller about publication') }}</div>
                                                    <div class="text-sm text-surface-600">{{ $t('Seller will be informed when content is published') }}</div>
                                                </label>
                                            </div>
                                            
                                            <div class="flex items-start gap-3">
                                                <Checkbox
                                                    v-model="form.notify_client"
                                                    inputId="notify_client"
                                                    :binary="true"
                                                />
                                                <label for="notify_client" class="cursor-pointer">
                                                    <div class="font-medium text-surface-900">{{ $t('Notify client about steps') }}</div>
                                                    <div class="text-sm text-surface-600">{{ $t('Client will be informed about order progress and steps') }}</div>
                                                </label>
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Submit Button -->
                            <div class="col-12">
                                <Button
                                    type="submit"
                                    :label="$t('Update order')"
                                    icon="pi pi-save"
                                    :loading="form.processing"
                                    :disabled="isCanceled || form.processing || !isAllStepsComplete"
                                />
                                
                                <div v-if="form.sites.length > 0" class="text-sm mt-2">
                                    <div v-if="!form.client_id" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please select a client to continue') }}
                                    </div>
                                    <div v-else-if="!isFormValid" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please select a type for all sites to continue') }}
                                    </div>
                                    <div v-else-if="!isPaymentConfirmed" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please configure payment settings to continue') }}
                                    </div>
                                    <div v-else-if="!isPublicationReady" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please add content and set publication dates for all sites') }}
                                    </div>
                                    <div v-else-if="isAllStepsComplete" class="text-green-600">
                                        <i class="pi pi-check-circle mr-1"></i>
                                        {{ $t('Ready to update order!') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        </fieldset>
                    </form>
                </template>
            </Card>
        </div>
    </div>

    <!-- Content Modal -->
    <ContentModal
        v-model:visible="showContentModal"
        :header="currentItemIndex !== null ? (itemContents[currentItemIndex] ? $t('Edit Content') : $t('Add Content')) : $t('Add Content')"
        :site-features="siteFeatures"
        :max-anchors="maxAnchorsComputed"
        :selected-order-item="selectedOrderItem"
        :languages="formattedLanguages"
        :submitting="submittingContent"
        :initial-data="currentItemIndex !== null ? (itemContents[currentItemIndex] || {}) : {}"
        :hide-auto-content="true"
        :allow-skip="true"
        @submit="onContentSubmit"
        @close="closeContentModal"
    />

    <!-- Cancel item / Add item dialogs (immediate actions on saved items) -->
    <OrderItemActionsDialogs v-bind="itemActions" />

</template>

<style scoped>
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.currency-selector :deep(.p-select) {
    height: 41px;
}

/* Order items table: header titles must always stay on a single line */
.order-items-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.order-items-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

/* Add-sites dialog table: header titles must always stay on a single line */
.add-sites-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.add-sites-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}
</style>
